import React from "react";

/**
 * Reusable ranked analytics table.
 *
 * Props:
 *   title       - card header title
 *   subtitle    - card header subtitle
 *   badge       - pill badge text (e.g. "Top 10")
 *   badgeColor  - Tailwind color key: "blue" | "emerald" | "violet" | "indigo" | "amber"
 *   columns     - [{ label, align? }]   align: "left" (default) | "right" | "center"
 *   rows        - array of row objects
 *   renderRow   - (row, index, rows) => <td> elements (no <tr> wrapper needed — component adds it)
 *   colSpan     - number of columns (for empty state colspan)
 *   emptyText   - text when rows is empty
 */

const BADGE_STYLES = {
  blue:    "bg-blue-50 text-blue-600",
  emerald: "bg-emerald-50 text-emerald-600",
  violet:  "bg-violet-50 text-violet-600",
  indigo:  "bg-indigo-50 text-indigo-600",
  amber:   "bg-amber-50 text-amber-600",
};

const RankedTable = ({
  title,
  subtitle,
  badge,
  badgeColor = "blue",
  columns = [],
  rows = [],
  renderRow,
  colSpan,
  emptyText = "No data available",
}) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
    {/* Header */}
    {(title || subtitle || badge) && (
      <div className="flex items-center justify-between p-3 border-b border-gray-100 bg-gray-100">
        <div className="flex flex-col">
          <div className="text-sm font-semibold text-gray-800">{title}</div>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
        {badge && (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${BADGE_STYLES[badgeColor] || BADGE_STYLES.blue}`}>
            {badge}
          </span>
        )}
      </div>
    )}

    {/* Table */}
    <table className="w-full text-xs modern-table">
      {columns.some(c => c.width) && (
        <colgroup>
          {columns.map((col, i) => <col key={i} style={col.width ? { width: col.width } : {}} />)}
        </colgroup>
      )}
      <thead>
        <tr className="border-b border-gray-100">
          {columns.map((col, i) => (
            <th
              key={i}
              className={`px-5 py-3 font-medium uppercase tracking-wide text-[10px] ${
                col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"
              }`}
            >
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {rows.length === 0 ? (
          <tr>
            <td colSpan={colSpan || columns.length} className="px-5 py-6 text-center text-gray-400">
              {emptyText}
            </td>
          </tr>
        ) : (
          rows.map((row, i) => (
            <tr key={i} className="hover:bg-gray-50 transition-colors">
              {renderRow(row, i, rows)}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export default RankedTable;
